import * as Sentry from "@sentry/react";
import firebaseSettings from "../emulatorConfig";

/**
 * ログレベル
 */
export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

/**
 * ログ設定
 */
interface LoggerConfig {
  enableConsole: boolean;
  enableSentry: boolean;
  environment: string;
}

/**
 * ロガー設定の初期化
 */
const config: LoggerConfig = {
  enableConsole: true, // 開発環境では常に有効
  enableSentry: !firebaseSettings.isEmulator, // 本番環境のみSentry有効
  environment: firebaseSettings.isEmulator ? "development" : "production",
};

/**
 * Sentryの初期化
 */
export const initSentry = (dsn?: string) => {
  if (!dsn) {
    console.warn("⚠️ Sentry DSN is not provided. Sentry will not be initialized.");
    return;
  }

  Sentry.init({
    dsn,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // パフォーマンスモニタリング
    tracesSampleRate: config.environment === "production" ? 0.1 : 1.0,
    // セッションリプレイ
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // エラーフィルタリング
    beforeSend(event, _hint) {
      // 開発環境ではSentryに送信しない
      if (config.environment !== "production") {
        return null;
      }
      return event;
    },
  });

  console.log("✅ Sentry initialized");
};

/**
 * ログ出力の内部実装
 */
const log = (level: LogLevel, message: string, data?: unknown) => {
  const timestamp = new Date().toISOString();
  const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

  // コンソール出力
  if (config.enableConsole) {
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(logMessage, data || "");
        break;
      case LogLevel.INFO:
        console.log(logMessage, data || "");
        break;
      case LogLevel.WARN:
        console.warn(logMessage, data || "");
        break;
      case LogLevel.ERROR:
        console.error(logMessage, data || "");
        break;
    }
  }

  // Sentryへの送信
  if (config.enableSentry) {
    if (level === LogLevel.ERROR) {
      if (data instanceof Error) {
        Sentry.captureException(data, {
          tags: { message },
        });
      } else {
        Sentry.captureMessage(message, {
          level: "error",
          extra: data ? { data } : undefined,
        });
      }
    } else if (level === LogLevel.WARN) {
      Sentry.captureMessage(message, {
        level: "warning",
        extra: data ? { data } : undefined,
      });
    }
  }
};

/**
 * Logger クラス
 */
export class Logger {
  /**
   * デバッグログ
   */
  static debug(message: string, data?: unknown) {
    log(LogLevel.DEBUG, message, data);
  }

  /**
   * 情報ログ
   */
  static info(message: string, data?: unknown) {
    log(LogLevel.INFO, message, data);
  }

  /**
   * 警告ログ
   */
  static warn(message: string, data?: unknown) {
    log(LogLevel.WARN, message, data);
  }

  /**
   * エラーログ
   */
  static error(message: string, error?: Error | unknown) {
    log(LogLevel.ERROR, message, error);
  }

  /**
   * ユーザー情報をSentryに設定
   */
  static setUser(user: { id: string; email?: string; role?: string }) {
    if (config.enableSentry) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        role: user.role,
      });
    }
  }

  /**
   * ユーザー情報をクリア
   */
  static clearUser() {
    if (config.enableSentry) {
      Sentry.setUser(null);
    }
  }

  /**
   * カスタムコンテキストを追加
   */
  static setContext(key: string, value: Record<string, unknown>) {
    if (config.enableSentry) {
      Sentry.setContext(key, value);
    }
  }

  /**
   * タグを追加
   */
  static setTag(key: string, value: string) {
    if (config.enableSentry) {
      Sentry.setTag(key, value);
    }
  }
}

export default Logger;
